import React from "react";
import { Button, Spinner } from "@fluentui/react-components";
import {
  ArrowRightRegular,
  GlobeRegular,
  InfoRegular,
  ShieldCheckmarkRegular,
} from "@fluentui/react-icons";
import { useSignInGateI18n } from "./SignInGate.i18n";
import { useSignInGateViewModel } from "./SignInGate.ViewModel";
import type { SignInGateViewModelArgs } from "./SignInGate.ViewModel.types";
import SignInGateNetwork from "./SignInGateNetwork";
import "./SignInGate.scss";

const SignInGate: React.FC<SignInGateViewModelArgs> = (props) => {
  const {
    hasFailed,
    popupBlocked,
    isBusy,
    showsPopupHint,
    logoSuffix,
    websiteUrl,
    handleSignIn,
    handleWebsiteClick,
  } = useSignInGateViewModel(props);
  const i18n = useSignInGateI18n();

  return (
    <div className="sign-in-gate">
      <section className="sign-in-gate__card">
        <img
          className="sign-in-gate__logo"
          src={`/assets/img/logo_full_horizontal${logoSuffix}_32.png`}
          alt={i18n.logoAlt}
        />

        <h1 className="sign-in-gate__title">
          {popupBlocked ? (
            i18n.blockedTitle
          ) : hasFailed ? (
            i18n.failedTitle
          ) : (
            <>
              {i18n.sloganLead}{" "}
              <span className="sign-in-gate__title-accent">
                {i18n.sloganAccent}
              </span>
            </>
          )}
        </h1>
        <p className="sign-in-gate__lead">
          {popupBlocked
            ? i18n.blockedLead
            : hasFailed
              ? i18n.failedLead
              : i18n.lead}
        </p>

        {!hasFailed && !popupBlocked && (
          <SignInGateNetwork
            label={i18n.networkAria}
            logoSrc="/assets/img/logo_32x32.png"
          />
        )}

        <Button
          appearance="primary"
          size="large"
          className="sign-in-gate__action"
          disabled={isBusy}
          onClick={handleSignIn}
          icon={isBusy ? <Spinner size="tiny" /> : undefined}
        >
          {isBusy
            ? i18n.signingIn
            : hasFailed || popupBlocked
              ? i18n.tryAgain
              : i18n.signIn}
        </Button>

        {showsPopupHint && (
          <div className="sign-in-gate__notice">
            <span className="sign-in-gate__notice-icon" aria-hidden="true">
              <InfoRegular />
            </span>
            <div>
              <strong className="sign-in-gate__notice-title">
                {i18n.popupHintTitle}
              </strong>
              <p className="sign-in-gate__notice-body">{i18n.popupHintBody}</p>
            </div>
          </div>
        )}

        <div className="sign-in-gate__trust">
          <div className="sign-in-gate__trust-item">
            <span className="sign-in-gate__trust-icon" aria-hidden="true">
              <ShieldCheckmarkRegular />
            </span>
            <div>
              <strong className="sign-in-gate__trust-title">
                {i18n.secureTitle}
              </strong>
              <p className="sign-in-gate__trust-body">{i18n.secureBody}</p>
            </div>
          </div>

          <div className="sign-in-gate__trust-item">
            <span className="sign-in-gate__trust-icon" aria-hidden="true">
              <GlobeRegular />
            </span>
            <div>
              <strong className="sign-in-gate__trust-title">
                {i18n.residencyTitle}
              </strong>
              <p className="sign-in-gate__trust-body">{i18n.residencyBody}</p>
            </div>
          </div>
        </div>

        <a
          className="sign-in-gate__learn-more"
          href={websiteUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleWebsiteClick}
        >
          {i18n.learnMore}
          <ArrowRightRegular />
        </a>
      </section>
    </div>
  );
};

export default SignInGate;
